"use client";

import { IResource } from "@gettactic/api";
import React, { Dispatch, createContext, useContext, useReducer } from "react";

export type ResourceSettingsState = {
  selected: IResource | null;
};

export const defaultInitialState: ResourceSettingsState = {
  selected: null
};

export enum ResourceSettingsActionType {
  SetSelectedResource = "set-selected-resource"
}
type ResourceSettingsAction = {
  type: ResourceSettingsActionType.SetSelectedResource;
  payload: ResourceSettingsState["selected"];
};

const ResourceSettingsContext = createContext<
  [ResourceSettingsState, Dispatch<ResourceSettingsAction>]
>(null!);
ResourceSettingsContext.displayName = "ResourceSettingsContext";

function reducer(
  state: ResourceSettingsState,
  action: ResourceSettingsAction
): ResourceSettingsState {
  switch (action.type) {
    case ResourceSettingsActionType.SetSelectedResource: {
      return {
        ...state,
        selected: action.payload
      };
    }
    default: {
      throw new Error(
        "Reducer in useResourceSettingsState: not all ResourceSettingsActionType's are handled"
      );
    }
  }
}

export function ResourceSettingsProvider({
  children,
  initial
}: React.PropsWithChildren<{
  initial?: ResourceSettingsState;
}>) {
  const [state, dispatch] = useReducer(reducer, initial ?? defaultInitialState);

  return (
    <ResourceSettingsContext.Provider value={[state, dispatch]}>
      {children}
    </ResourceSettingsContext.Provider>
  );
}

export interface ResourceSettingsContext {
  state: ResourceSettingsState;
  dispatch: React.Dispatch<ResourceSettingsAction>;
}
export function useResourceSettingsState(): ResourceSettingsContext {
  const [state, dispatch] = useContext(ResourceSettingsContext);
  if (state === undefined) {
    throw new Error(
      "useResourceSettingsState must be used within a ResourceSettingsProvider"
    );
  }
  return { state, dispatch };
}
